import { useQuery } from 'react-query'
import { useEnvVars } from './useEnvVars'

/**
 * Wire shape of the Quiqup Platform places response. The service returns the
 * emirate's cities (Quiqup's "areas") as `{ id, name }` pairs; the pin-confirm
 * Area field only needs the display `name`s.
 */
type PlatformCity = {
  id: number
  name: string
}

type PlatformCitiesResponse = {
  cities?: PlatformCity[]
}

/**
 * Fetches the list of cities (areas) for a UAE emirate from the internal Quiqup
 * Platform places API, to power the Area field's typeahead on the public
 * pin-confirm page (story sc-27719, Phase-2 of sc-26879).
 *
 * Contract (Quiqup Platform, Encore `//encore:api public`):
 *   GET {QUIQUP_PLATFORM_URL}/countries/AE/states/{emirate}/cities
 *   -> 200 { cities: [ { id, name } ] }
 *   -> 404 when the emirate isn't a recognised state (e.g. free-text the
 *      customer typed) — treated as "no suggestions", never an error the
 *      customer sees.
 *
 * The `states` path segment is the emirate name, URL-encoded (matching
 * QuiqDash v2's usage, e.g. `.../states/Al%20Ain/cities`).
 *
 * This is a PUBLIC page: the customer has no login/token, so the request is
 * sent with NO Authorization header — verified callable unauthenticated against
 * the public Encore endpoint. Raw `fetch` (not this app's `ApiClient`) is used
 * for the same reason as `useConfirmPinLocation`: it is a cross-origin call to a
 * non-core host and we want a trivial CORS footprint.
 *
 * We send NO request headers at all — deliberately. A bare cross-origin GET
 * with only CORS-safelisted headers is a "simple request", so the browser skips
 * the OPTIONS preflight. Adding `Content-Type: application/json` (not safelisted,
 * and pointless on a bodyless GET) would force a preflight the Platform API may
 * not answer for this path, silently failing the call in a real browser (curl
 * doesn't preflight, so it wouldn't show up there). This mirrors the sibling
 * `useGetShipment`, which hits the same `QUIQUP_PLATFORM_URL` host header-free.
 *
 * Suggestions are advisory only — the Area field stays free-text (Nadim's
 * requirement), so any failure resolves to an empty option list and never
 * blocks the customer from typing.
 */
export function useCitiesForEmirate(emirate?: string) {
  const { env } = useEnvVars()

  const trimmedEmirate = emirate?.trim()
  const baseUrl = env.QUIQUP_PLATFORM_URL

  const { data } = useQuery<string[]>(
    ['platform-cities', 'AE', trimmedEmirate],
    async () => {
      if (!baseUrl || !trimmedEmirate) {
        return []
      }

      const url = `${baseUrl}/countries/AE/states/${encodeURIComponent(
        trimmedEmirate
      )}/cities`

      let response: Response
      try {
        // No headers: keep it a simple (preflight-free) cross-origin GET.
        response = await fetch(url)
      } catch {
        // Network/transport failure — degrade to no suggestions.
        return []
      }

      if (!response.ok) {
        // 404 (unknown emirate) or any other status — no suggestions.
        return []
      }

      const body = (await response.json()) as PlatformCitiesResponse
      return (body.cities ?? []).map((city) => city.name)
    },
    {
      // Only fetch once an emirate is selected and the platform URL is known.
      enabled: !!baseUrl && !!trimmedEmirate,
      // Area lists are effectively static reference data; avoid refetch churn.
      staleTime: Infinity,
      // Never surface a failure to the customer — degrade to empty suggestions.
      retry: false,
    }
  )

  return { cities: data ?? [] }
}
